"use client";

import React, { createContext, useContext, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { v4 as uuidv4 } from "uuid";
import { Toast, ToastProps, ToastType } from "./toast";

// Define a toast item type with all properties needed for display
interface ToastItem {
  id: string;
  title: string;
  description?: string;
  type: ToastType;
  duration: number;
}

// Define the context value type
interface ToastContextValue {
  toasts: ToastItem[];
  addToast: (toast: Omit<ToastItem, "id">) => string;
  removeToast: (id: string) => void;
  removeAllToasts: () => void;
}

// Create context with default values
const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  addToast: () => "",
  removeToast: () => {},
  removeAllToasts: () => {},
});

// Hook to use the toast context
export const useToast = () => {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  
  return context;
};

// Define props for the provider component
interface ToastProviderProps {
  children: React.ReactNode;
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left" | "top-center" | "bottom-center";
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = "top-right",
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Function to add a new toast
  const addToast = useCallback(
    ({ title, description, type = "info", duration = 5000 }: Omit<ToastItem, "id">) => {
      const id = uuidv4();
      
      setToasts((prevToasts) => {
        // If we're at the max number of toasts, remove the oldest one
        const newToasts =
          prevToasts.length >= maxToasts
            ? prevToasts.slice(1)
            : [...prevToasts];
        
        // Add the new toast
        return [...newToasts, { id, title, description, type, duration }];
      });
      
      return id;
    },
    [maxToasts]
  );

  // Function to remove a toast by ID
  const removeToast = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  // Function to remove all toasts
  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Generate position styles based on the position prop
  const getPositionStyles = () => {
    switch (position) {
      case "top-right":
        return "top-0 right-0";
      case "top-left":
        return "top-0 left-0";
      case "bottom-right":
        return "bottom-0 right-0";
      case "bottom-left":
        return "bottom-0 left-0";
      case "top-center":
        return "top-0 left-1/2 transform -translate-x-1/2";
      case "bottom-center":
        return "bottom-0 left-1/2 transform -translate-x-1/2";
      default:
        return "top-0 right-0";
    }
  };

  return (
    <ToastContext.Provider
      value={{ toasts, addToast, removeToast, removeAllToasts }}
    >
      {children}
      <div
        className={`fixed z-50 m-4 flex flex-col gap-2 ${getPositionStyles()}`}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <AnimatePresence>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              id={toast.id}
              title={toast.title}
              description={toast.description}
              type={toast.type}
              duration={toast.duration}
              onClose={removeToast}
            />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};
